<template>
    <div id="merge-report-modal" class="flex justify-center items-center">
        <div @click="closeByBackdrop" class="backdrop"></div>
        <div class="modal-content relative">
            <InnerLoader v-if="isLoading" color="var(--text-color)" />
            <div class="modal-header flex justify-between items-center">
                <h1>{{ $t('merge_report_header') }}</h1>
                <div @click="closeModal" class="close-button flex justify-center items-center cursor-pointer">
                    <i class="fas fa-times"></i>
                </div>
            </div>
            <div class="modal-body">
                <div class="flex justify-end mb-4">
                    <span @click="openMergeReportSelect" class="cursor-pointer">
                        <i class="fas fa-plus"></i> {{ $t('merge_report_new_button') }}
                    </span>
                </div>
                <div v-if="mergeReportLists && mergeReportLists.length" class="scroll-box w-full">
                    <div
                        v-for="(item, index) in mergeReportLists"
                        :key="index"
                        class="text-dark-charcoal system-border bg-white block md:flex md:flex-wrap lg:flex-no-wrap justify-between border-list p-2 mb-2"
                    >
                        <div class="flex justify-start items-center w-auto mb-2 sm:mb-0">
                            <div>
                                <strong class="block font-size-16">{{ item.name }}</strong>
                                <span class="font-size-14 shadow-text-none">
                                    {{ item.created_at_readable }} | {{ $t('merge_report_combined_reports') }}:
                                    {{ item.reports.length }}
                                    |
                                    {{
                                        $t('merge_report_display_entries', {
                                            ranking: item.display_ranking,
                                            total: item.total_player_in_reports
                                        })
                                    }}
                                </span>
                            </div>
                        </div>
                        <div class="action-btn-box flex justify-center lg:justify-end items-center w-auto mt-2 md:mt-0">
                            <XToggleSwitch
                                @input="updateMergeReportStatus(item._id.$oid, $event.target.checked)"
                                :checked="item.status"
                                :border-checked-active="true"
                                class="mr-2 mt-1 shadow-text-none"
                                height="20"
                                color="#5a5351"
                                border-weight="3"
                                border-color="#5a5351"
                                checked-color="#28a745"
                            />

                            <XButton
                                v-tippy="{ placement: 'top' }"
                                @click="openReportURL(item.report_url)"
                                :disabled="!item.status || !item.reports.length"
                                :content="$t('report_view_quiz_result')"
                                bg-color="transparent"
                                color="#333"
                                class="mr-2 mt-1"
                            >
                                <i class="fas fa-poll"></i>
                            </XButton>
                            <XButton
                                v-tippy="{ placement: 'top' }"
                                @click="openEditMergeReport(item)"
                                :content="$t('my_quiz_edit')"
                                bg-color="transparent"
                                color="#333"
                                class="mr-2"
                            >
                                <i class="fas fa-edit"></i>
                            </XButton>
                            <XButton
                                @click="onDeleteMergeReport(item._id.$oid)"
                                bg-color="#dc3545"
                                class="delete-button"
                            >
                                <i class="fas fa-trash-alt"></i>
                            </XButton>
                        </div>
                    </div>
                </div>
                <div v-else class="no-merge-report flex flex-col justify-center items-center w-full">
                    <i class="fas fa-scroll system-text-color system-text-shadow font-size-40 md:font-size-56"></i>
                    <h4 class="system-text-color system-text-shadow text-center md:font-size-18 m-0 my-4">
                        {{ $t('merge_report_no_merge_report') }}
                    </h4>
                </div>
            </div>
            <div v-if="mergeReportLists && mergeReportLists.length" class="flex justify-center items-center">
                <Paginator
                    @pagination-change="onPageChange"
                    :current-page="currentPage"
                    :page-count="pageCount"
                    :items-per-page="itemsPerPage"
                    :total-items="totalItems"
                    ref="setPage"
                />
            </div>
        </div>
        <MergeReportSelect
            v-if="isOpenMergeReportSelect"
            :is-edit="isEdit"
            :merge-report-id="mergeReportId"
            @close="isOpenMergeReportSelect = false"
            @success="onCloseMergeReportSelect"
        />
    </div>
</template>

<script>
import XButton from '@/components/v2/XButton'
import popup from '@/lib/popup'
import MergeReportSelect from '@/components/merge-report/MergeReportSelect'
import Paginator from '@/components/Paginator'
import InnerLoader from '@/components/InnerLoader'
import XToggleSwitch from '@/components/v2/XToggleSwitch'

export default {
    components: {
        XButton,
        MergeReportSelect,
        Paginator,
        InnerLoader,
        XToggleSwitch
    },

    data() {
        return {
            mergeReportLists: [],
            currentPage: 1,
            pageCount: 0,
            itemsPerPage: 8,
            totalItems: 0,
            isLoading: false,
            isOpenMergeReportSelect: false,
            isEdit: false,
            mergeReportId: null
        }
    },

    mounted() {
        this.getMergeReports()
    },

    methods: {
        closeModal() {
            this.$emit('close')
        },

        closeByBackdrop(e) {
            if (!e.target.classList.contains('backdrop')) {
                return
            }

            this.closeModal()
        },

        async getMergeReports() {
            this.isLoading = true

            const urlParams = new URL(window.location.href)

            this.currentPage = urlParams.searchParams.get('merge_report_page') || 1

            try {
                const res = await this.$store.dispatch('getMergeReports', {
                    params: {
                        page: this.currentPage,
                        perpage: this.itemsPerPage
                    }
                })

                this.mergeReportLists = res.data
                this.pageCount = parseInt(res.page_count)
                this.itemsPerPage = parseInt(res.perpage)
                this.totalItems = parseInt(res.total)

                this.isLoading = false
            } catch (err) {
                this.isLoading = false
                this.mergeReportLists = []
            }
        },

        openMergeReportSelect() {
            this.isEdit = false
            this.isOpenMergeReportSelect = true
        },

        openEditMergeReport(item) {
            this.mergeReportId = item._id.$oid
            this.isEdit = true
            this.isOpenMergeReportSelect = true
        },

        onDeleteMergeReport(id) {
            popup.confirm(this.$t('merge_report_confirm_delete'), async () => {
                try {
                    this.isLoading = true

                    const payload = {
                        id: id
                    }

                    await this.$store.dispatch('deleteMergeReport', payload)
                    this.toastSuccess(this.$t('merge_report_delete_success'))
                    this.setFirstPage()
                } catch {
                    this.toastError(this.$t('merge_report_delete_error'))
                    this.isLoading = false
                }
            })
        },

        onPageChange(value) {
            this.currentPage = value

            const urlParams = new URL(window.location.href)

            urlParams.searchParams.set('merge_report_page', this.currentPage)

            window.history.replaceState(null, null, urlParams.toString())

            this.getMergeReports()
        },

        setFirstPage() {
            this.onPageChange(1)
            if (this.$refs.setPage) {
                this.$refs.setPage.setPage(0)
            }
        },

        openReportURL(url) {
            window.open(url, '_blank')
        },

        onCloseMergeReportSelect(type) {
            if (type === 'update') {
                this.getMergeReports()
                return
            }

            this.setFirstPage()
            this.isOpenMergeReportSelect = false
        },

        async updateMergeReportStatus(id, status) {
            try {
                this.isLoading = true

                await this.$store.dispatch('updateMergeReportStatus', {
                    id: id,
                    status: status
                })

                this.mergeReportLists.forEach(item => {
                    if (item._id.$oid === id) {
                        item.status = status
                    }
                })

                this.toastSuccess(this.$t('merge_report_update_success'))
                this.isLoading = false
            } catch {
                this.toastError(this.$t('merge_report_update_error'))
                this.isLoading = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
#merge-report-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;

    .backdrop {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background-color: #0006;
        z-index: -1;
    }

    .modal-content {
        display: flex;
        flex-direction: column;
        width: 800px;
        border-radius: 6px;
        color: #333;
        background-color: #fff;
        margin: 0.5rem;

        .modal-header {
            padding: 14px 14px 0 14px;

            h1 {
                margin: 0;
                font-size: 17.5px;
            }

            .close-button {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                color: #6c757d;
                transition: color 0.2s, background-color 0.2s;

                &:hover {
                    color: #343a40;
                    background: #e9ecef;
                }
            }
        }

        .modal-body {
            padding: 14px;

            .scroll-box {
                max-height: calc(100vh - 300px);
                overflow-y: auto;

                @media screen and (max-width: 575px) {
                    max-height: calc(100vh - 350px);
                }
            }

            .no-merge-report {
                height: 200px;
                border: 2px solid var(--ui-primary-color);
                border-radius: 0.5rem;
                padding: 1rem;
            }

            .border-list {
                border-radius: 4px;
            }

            .action-btn-box {
                .x-button {
                    max-width: 30px;
                    height: 34px;
                    font-size: 22px;
                }

                .delete-button {
                    max-width: 60px;
                    font-size: 20px;
                }

                @media screen and (max-width: 575px) {
                    .delete-button {
                        max-width: 40px;
                        font-size: 18px;
                    }
                }
            }
        }
    }
}
</style>
